import React from 'react';
import { pipe } from 'lodash/fp';
import { Redirect, useHistory } from 'react-router-dom';
import { CURRENT_LOCALE } from '@whitelabel/helpers/constants';
import { dateISOtoDDMMMYYYY } from '@whitelabel/helpers/date';
import { ISectionField, IFNOLSection, IAllSectionFields, IAllClaimAnswersValues } from '@whitelabel/helpers/types';
import { SAVED_CLAIMS } from './constants';
import { groupFieldsByGroupTypeAndName, processGroupFields, processMetaDisplayFields } from './fieldData';
import { getLocalStorageValueOrNull } from './utils';

export const getURLQueryParams = (search: any) => {
  const urlParams = new URLSearchParams(search);
  const bookingID = urlParams.get('bookingID');
  const claimSelectorID = urlParams.get('claimSelectorID');
  const currentDecisionPointId = urlParams.get('currentDecisionPointId');
  const quoteID = urlParams.get('quoteID');
  const securityToken = urlParams.get('security_token');
  const claimTypeID = urlParams.get('claimTypeID');
  const claimID = urlParams.get('claimID');
  const hasClaimGuidelines = urlParams.get('claimGuidelines') === 'true';
  const customerID = urlParams.get('customerID');
  const skipBenefitValidation = urlParams.get('skipBenefitValidation');

  return {
    bookingID,
    quoteID,
    claimSelectorID,
    currentDecisionPointId,
    securityToken,
    claimTypeID,
    claimID,
    hasClaimGuidelines,
    customerID,
    skipBenefitValidation,
  };
};

interface INavigateToClaimQuestionsPageArgs {
  decisionPointId: string;
  history: ReturnType<typeof useHistory>;
  locale: string;
  bookingID: string;
  quoteID: string;
  securityToken: string | null;
  hasClaimGuidelines?: boolean;
  replace?: boolean;
}

export const navigateToClaimQuestionPage = ({
  decisionPointId,
  history,
  locale,
  bookingID,
  quoteID,
  securityToken,
  hasClaimGuidelines = false,
  replace = false,
}: INavigateToClaimQuestionsPageArgs): void => {
  const searchParams = new URLSearchParams({ bookingID, quoteID });
  if (securityToken) {
    searchParams.append('security_token', securityToken);
  } else if (hasClaimGuidelines) {
    searchParams.append('claimGuidelines', 'true');
  }

  if (replace) {
    history.replace(`/${locale}/account/claims/fnol/questions/${decisionPointId}?${searchParams}`);
  } else {
    history.push(`/${locale}/account/claims/fnol/questions/${decisionPointId}?${searchParams}`);
  }
};

export const redirectIfDifferentLocale = () => {
  const currentLocale = sessionStorage.getItem(CURRENT_LOCALE);
  const [, lang, pathRemainder] =
    /\/?(.+?)(\/.+)$/.exec(window.location.href.substring(window.location.origin.length)) || [];
  if (currentLocale && currentLocale !== lang) {
    const correctUrl = `/${currentLocale}${pathRemainder || ''}`;
    return <Redirect to={correctUrl} />;
  }
  return null;
};

export const transformSectionFields = (sectionFields: ISectionField[]): ISectionField[] =>
  pipe(groupFieldsByGroupTypeAndName, processGroupFields, processMetaDisplayFields)(sectionFields);

export const getFNOLFields = (sections: IFNOLSection[]) =>
  sections.reduce((fnolFieldsObj: IAllSectionFields, section: IFNOLSection) => {
    const customSectionFields = transformSectionFields(section.sectionFields);
    fnolFieldsObj[section.name] = customSectionFields.reduce(
      (sectionFieldsObj: IAllSectionFields['sectionName'], sectionField: ISectionField) => {
        sectionFieldsObj[sectionField.name] = sectionField;
        return sectionFieldsObj;
      },
      {},
    );
    return fnolFieldsObj;
  }, {});

interface IGetSavedClaimValuesArgs {
  bookingID: string;
  values?: any;
  savedTime?: string;
  decisionPointChanged?: boolean;
}

export const getSavedClaimValues = ({
  bookingID,
  values,
  savedTime,
  decisionPointChanged,
}: IGetSavedClaimValuesArgs): IAllClaimAnswersValues => {
  const prevValues = getLocalStorageValueOrNull<IAllClaimAnswersValues>(SAVED_CLAIMS);
  if (prevValues) {
    // if localStorage already has values, keep previous values and overwrite certain fields for this bookingID.
    return {
      ...prevValues,
      [bookingID]: {
        sectionFields: decisionPointChanged
          ? {}
          : {
              ...prevValues[bookingID]?.sectionFields,
              ...values?.sectionFields,
            },
        savedTime,
        /**
         * if value coming from claim answers section => save the value
         * if value coming from fnol answers section => ignore the value for "claimSelectorAnswers"
         *  */
        claimSelectorAnswers: !values?.sectionFields
          ? {
              ...prevValues[bookingID]?.claimSelectorAnswers,
              ...values,
            }
          : { ...prevValues[bookingID]?.claimSelectorAnswers },
      },
    };
  }
  // if localStorage is empty then initialize a basic data structure for storage.
  return {
    [bookingID]: {
      sectionFields: {
        ...values?.sectionFields,
      },
      savedTime,
      claimSelectorAnswers: !values?.sectionFields
        ? {
            values,
          }
        : {},
    },
  };
};

const deleteClaimValues = (bookingID: string): IAllClaimAnswersValues => {
  const prevValues = getLocalStorageValueOrNull<IAllClaimAnswersValues>(SAVED_CLAIMS);
  // if localStorage has related files with this bookingID, delete them all.
  Object.keys(localStorage).forEach((key) => {
    if (key.endsWith(`_${bookingID}_files`)) localStorage.removeItem(key);
  });
  // if localStorage already has values, filter out this bookingID and keep rest bookingIDs.
  if (prevValues) {
    const filteredPrevValues = Object.keys(prevValues)
      .filter((key) => key !== bookingID)
      .reduce(
        (obj, key) => ({
          ...obj,
          [key]: prevValues[key],
        }),
        {},
      );
    return {
      ...filteredPrevValues,
    };
  }
  // if localStorage is empty then initialize a basic data structure for storage.
  return {
    [bookingID]: { sectionFields: {}, savedTime: undefined, claimSelectorAnswers: {} },
  };
};

interface ISaveOrDeleteClaimValuesArgs {
  bookingID: string;
  isToSaveValues: boolean;
  values?: any;
  decisionPointChanged?: boolean;
}

export const saveOrDeleteClaimValues = ({
  bookingID,
  isToSaveValues,
  values,
  decisionPointChanged = false,
}: ISaveOrDeleteClaimValuesArgs): void => {
  const savedTime = dateISOtoDDMMMYYYY(new Date().toISOString());

  localStorage.setItem(
    SAVED_CLAIMS,
    JSON.stringify(
      isToSaveValues
        ? getSavedClaimValues({ bookingID, values, savedTime, decisionPointChanged })
        : deleteClaimValues(bookingID),
    ),
  );
};

export const cleanSectionFields = (bookingID: string | null, lastClaimTypeID: string): void => {
  if (bookingID) {
    saveOrDeleteClaimValues({
      bookingID,
      isToSaveValues: true,
      decisionPointChanged: true,
    });
    Object.keys(localStorage).forEach((key) => {
      if (key.endsWith(`_${bookingID}_files`) && !key.includes(lastClaimTypeID)) localStorage.removeItem(key);
    });
  }
};
